/**
 * Utility functions for file manager operations
 */

/**
 * Format file size for display with configurable options
 *
 * @param bytes - The file size in bytes
 * @param options - Formatting options
 * @returns Formatted file size string
 *
 * @example
 * formatFileSize(0) // '0 B'
 * formatFileSize(0, { zeroDisplay: '-' }) // '-'
 * formatFileSize(1536) // '1.5 KB'
 * formatFileSize(1536, { decimals: 2 }) // '1.50 KB'
 */
export const formatFileSize = (
  bytes: number,
  options: {
    /** String to display when bytes is 0 (default: '0 B') */
    zeroDisplay?: string;
    /** Number of decimal places (default: auto - 0 for bytes, 1 for others) */
    decimals?: number;
    /** Unit labels (default: ['B', 'KB', 'MB', 'GB', 'TB']) */
    units?: string[];
  } = {}
): string => {
  const {
    zeroDisplay = '0 B',
    decimals,
    units = ['B', 'KB', 'MB', 'GB', 'TB'],
  } = options;

  if (bytes === 0) return zeroDisplay;

  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  const size = bytes / Math.pow(1024, i);

  // Auto decimals: 0 for bytes, 1 for others (or use provided value)
  const decimalPlaces = decimals !== undefined ? decimals : i === 0 ? 0 : 1;

  return `${size.toFixed(decimalPlaces)} ${units[i] || 'B'}`;
};
